import { useEffect, useState, useMemo, memo, useRef } from "react";
import ReactDiffViewer from "react-diff-viewer-continued";
import type { DataTablePageEvent, DataTableSortEvent } from "primereact/datatable";

import { Calendar } from "primereact/calendar";
import { Chart } from "primereact/chart";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Card } from "primereact/card";
import { Dialog } from "primereact/dialog";
import { Tag } from "primereact/tag";
import { Skeleton } from "primereact/skeleton";
import { Button } from "primereact/button";

import { themeClasses, chartColors } from "sj-ui-toolkit/src/atlas/utils";
import { StatTile } from "sj-ui-toolkit/src/atlas/components/StatTile/StatTile";
import {
  getGuardianClient,
  type MetricsSummaryResponse,
} from "sj-ui-toolkit/src/client/svcGuardianClient";
import { humanDate, humanDateWithoutTime } from "sj-ui-toolkit/src/utils/dates";
import { PERIODS } from "sj-ui-toolkit/src/constants/periods";
import { useToast } from "sj-ui-toolkit/src/hooks";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface Vulnerability {
  name?: string;
  cwe?: string | string[];
  description?: string;
  level?: string;
}

interface Scan {
  created_at?: string;
  status?: string;
  vulnerability_count?: number;
  vulnerabilities?: Vulnerability[];
  updated_instructions?: string;
  original_instructions?: string;
  scanner_type?: string;
}

interface AspenGuardianScansProps {
  showInstructions?: boolean;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const LEVEL_SEVERITY: Record<string, "danger" | "warning" | "info" | "secondary"> = {
  HIGH: "danger",
  error: "danger",
  MEDIUM: "warning",
  warning: "warning",
  LOW: "info",
  info: "info",
};

const LEVEL_LABEL: Record<string, string> = {
  HIGH: "HIGH",
  error: "HIGH",
  MEDIUM: "MEDIUM",
  warning: "MEDIUM",
  LOW: "LOW",
  info: "LOW",
};

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

const MemoizedVulnTimelineChart = memo(({ scans }: { scans: Scan[] }) => {
  const chartData = useMemo(() => {
    const sorted = [...scans]
      .filter(s => s.created_at)
      .sort((a, b) => new Date(a.created_at!).getTime() - new Date(b.created_at!).getTime());
    return {
      labels: sorted.map(s => humanDateWithoutTime(s.created_at!)),
      datasets: [
        {
          label: "Vulnerabilities",
          data: sorted.map(s => s.vulnerability_count ?? 0),
          fill: true,
          tension: 0.4,
          borderColor: chartColors.getDojoChartColors()[0],
          backgroundColor: chartColors.withChartFillOpacity(chartColors.getDojoChartColors()[0]),
          pointRadius: 3,
        },
      ],
    };
  }, [scans]);

  const chartOptions = useMemo(
    () => ({
      maintainAspectRatio: false,
      plugins: {
        legend: { display: false },
        tooltip: {
          enabled: true,
          displayColors: false,
          callbacks: {
            title: (ctx: { label?: string }[]) => ctx[0]?.label ?? "",
            label: (ctx: { parsed: { y: number } }) => `${ctx.parsed.y} vulnerabilities`,
          },
        },
      },
      scales: {
        x: { display: false },
        y: { display: false, beginAtZero: true },
      },
    }),
    []
  );

  return <Chart type="line" data={chartData} options={chartOptions} style={{ height: "120px" }} />;
});

MemoizedVulnTimelineChart.displayName = "MemoizedVulnTimelineChart";

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const AspenGuardianScans = ({ showInstructions = true }: AspenGuardianScansProps) => {
  const { showToast } = useToast();
  const [scans, setScans] = useState<Scan[]>([]);
  const [metrics, setMetrics] = useState<MetricsSummaryResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [metricsLoading, setMetricsLoading] = useState(false);
  const [days, setDays] = useState(30);
  const [selectedVulns, setSelectedVulns] = useState<Vulnerability[] | null>(null);
  const [selectedInstructions, setSelectedInstructions] = useState<Scan | null>(null);

  const [tableScans, setTableScans] = useState<Scan[]>([]);
  const [tableLoading, setTableLoading] = useState(true);
  const [tableTotalRecords, setTableTotalRecords] = useState(0);
  const [tablePage, setTablePage] = useState(0);
  const [tablePageSize, setTablePageSize] = useState(25);
  const [dateRange, setDateRange] = useState<Date[] | null>(null);
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");
  const initialTableLoadDone = useRef(false);

  // Initial load: first page + chart history (limit 500) + metrics
  useEffect(() => {
    const client = getGuardianClient();
    Promise.allSettled([
      client.getScanHistory({ limit: tablePageSize, offset: 0, sortOrder: "desc" }).promise,
      client.getScanHistory({ limit: 500 }).promise,
      client.getMetrics({ days }).promise,
    ]).then(([firstPageResult, historyResult, metricsResult]) => {
      if (firstPageResult.status === "fulfilled") {
        setTableScans((firstPageResult.value.scans ?? []) as Scan[]);
        setTableTotalRecords(firstPageResult.value.total ?? 0);
      }
      if (historyResult.status === "fulfilled") {
        setScans((historyResult.value.scans ?? []) as Scan[]);
      }
      if (metricsResult.status === "fulfilled") {
        setMetrics(metricsResult.value);
      }

      const anyFailed = [firstPageResult, historyResult, metricsResult].some(
        r => r.status === "rejected"
      );
      if (anyFailed) {
        console.error(
          "Failed to load some Guardian data:",
          [firstPageResult, historyResult, metricsResult]
            .filter((r): r is PromiseRejectedResult => r.status === "rejected")
            .map(r => r.reason)
        );
        showToast({
          severity: "error",
          summary: "Error",
          detail: "Failed to load Guardian data",
          life: 5000,
        });
      }

      initialTableLoadDone.current = true;
      setTableLoading(false);
      setLoading(false);
    });
  }, []);

  // Re-fetch metrics when period changes (after initial load)
  useEffect(() => {
    if (loading) return;
    setMetricsLoading(true);
    getGuardianClient()
      .getMetrics({ days })
      .promise.then(data => setMetrics(data))
      .catch((err: unknown) => {
        console.error("Failed to load Guardian metrics:", err);
        showToast({
          severity: "error",
          summary: "Error",
          detail: "Failed to load Guardian metrics",
          life: 5000,
        });
      })
      .finally(() => setMetricsLoading(false));
  }, [days, loading]);

  // Paginated table fetch (skips the initial page already loaded above)
  useEffect(() => {
    if (!initialTableLoadDone.current) return;
    if (dateRange !== null && (!dateRange[0] || !dateRange[1])) return;
    setTableLoading(true);
    const { promise, abort } = getGuardianClient().getScanHistory({
      limit: tablePageSize,
      offset: tablePage * tablePageSize,
      dateFrom: dateRange?.[0]?.toISOString(),
      dateTo: dateRange?.[1]?.toISOString(),
      sortOrder,
    });
    promise
      .then(data => {
        setTableScans((data.scans ?? []) as Scan[]);
        setTableTotalRecords(data.total ?? 0);
      })
      .catch((err: unknown) => {
        console.error("Failed to load scan history:", err);
        setTableScans([]);
        setTableTotalRecords(0);
      })
      .finally(() => setTableLoading(false));
    return abort;
  }, [tablePage, tablePageSize, dateRange, sortOrder]);

  const handleTablePageChange = (event: DataTablePageEvent) => {
    if (event.rows !== tablePageSize) {
      setTablePageSize(event.rows);
      setTablePage(0);
    } else {
      setTablePage(event.page ?? 0);
    }
  };

  const handleTableSort = (event: DataTableSortEvent) => {
    setSortOrder((event.sortOrder ?? -1) === 1 ? "asc" : "desc");
    setTablePage(0);
  };

  const scanMetrics = metrics?.scan_metrics;

  const filteredScans = useMemo(() => {
    const cutoff = new Date();
    cutoff.setDate(cutoff.getDate() - days);
    return scans.filter(s => s.created_at && new Date(s.created_at) >= cutoff);
  }, [scans, days]);

  const dateTemplate = (rowData: Scan) => {
    if (!rowData.created_at) return "--";
    return (
      <span title={humanDate(rowData.created_at)}>{humanDateWithoutTime(rowData.created_at)}</span>
    );
  };

  const statusTemplate = (rowData: Scan) => {
    if (!rowData.status) return "--";
    const severity = rowData.status === "success" ? "success" : "danger";
    return <Tag value={rowData.status} severity={severity} />;
  };

  const vulnCountTemplate = (rowData: Scan) => {
    const count = rowData.vulnerability_count ?? 0;
    if (count === 0) return <span className="ml-4 text-secondary">0</span>;
    return (
      <Button text onClick={() => setSelectedVulns(rowData.vulnerabilities ?? [])}>
        {count}
      </Button>
    );
  };

  const instructionsTemplate = (rowData: Scan) => {
    if (!rowData.updated_instructions) return <span className="ml-4">--</span>;
    return (
      <Button text onClick={() => setSelectedInstructions(rowData)}>
        View diff
      </Button>
    );
  };

  const normalizeCwe = (cwe: string | string[] | undefined) => {
    if (!cwe) return "--";
    return Array.isArray(cwe) ? cwe.join(", ") : cwe;
  };

  return (
    <>
      <div className="flex items-center justify-between mt-6 mb-2">
        <h4 className="atlas-h4">Overview</h4>
        <div className="flex gap-2">
          {PERIODS.map(p => (
            <Button
              key={p.label}
              label={p.label}
              size="small"
              outlined={days !== p.days}
              onClick={() => setDays(p.days)}
            />
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
        <Card className={themeClasses.bgElevation1}>
          {loading || metricsLoading ? (
            <Skeleton width="220px" height="80px" />
          ) : (
            <StatTile
              label="Scans with Vulnerabilities"
              value={scanMetrics?.scans_with_vulnerabilities ?? "--"}
              barColor={chartColors.getDojoChartColors()[0]}
            />
          )}
        </Card>
        <Card className={themeClasses.bgElevation1}>
          {loading || metricsLoading ? (
            <Skeleton width="220px" height="80px" />
          ) : (
            <StatTile
              label="Instruction Updates"
              value={scanMetrics?.instructions_updated_count ?? "--"}
              barColor={chartColors.getDojoChartColors()[1]}
            />
          )}
        </Card>
        <Card className={themeClasses.bgElevation1}>
          {loading || metricsLoading ? (
            <Skeleton width="220px" height="80px" />
          ) : (
            <StatTile
              label="Total Vulnerabilities Found"
              value={scanMetrics?.total_vulnerability_count ?? "--"}
              barColor={chartColors.getDojoChartColors()[2]}
            />
          )}
        </Card>
      </div>

      <Card className={`mb-4 ${themeClasses.bgElevation1}`}>
        <p className="font-semibold mb-3">Vulnerabilities Over Time</p>
        {loading ? (
          <Skeleton className="w-full h-32" />
        ) : filteredScans.length === 0 ? (
          <p className="body-sm text-secondary">No data</p>
        ) : (
          <div style={{ height: "120px" }}>
            <MemoizedVulnTimelineChart scans={filteredScans} />
          </div>
        )}
      </Card>

      <h4 className="atlas-h4 mt-6 mb-4">Scan History</h4>
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <Calendar
          value={dateRange ?? undefined}
          onChange={e => {
            setDateRange((e.value as Date[] | null) ?? null);
            setTablePage(0);
          }}
          selectionMode="range"
          placeholder="Select date range"
          showButtonBar
          hideOnRangeSelection
          dateFormat="mm/dd/yy"
          maxDate={new Date()}
          className="w-56"
        />
        <Button
          label="Clear Filters"
          icon="pi pi-filter-slash"
          severity="secondary"
          onClick={() => {
            setDateRange(null);
            setTablePage(0);
          }}
        />
      </div>

      <Card className={themeClasses.bgElevation1}>
        <DataTable
          value={tableLoading ? Array(tablePageSize).fill(null) : tableScans}
          aria-label="Scan History"
          className="p-datatable-sm"
          tableClassName="dt-responsive-text"
          sortMode="single"
          sortField="created_at"
          sortOrder={sortOrder === "asc" ? 1 : -1}
          onSort={handleTableSort}
          emptyMessage="No data"
          lazy
          paginator
          rows={tablePageSize}
          totalRecords={tableTotalRecords}
          first={tablePage * tablePageSize}
          onPage={handleTablePageChange}
          rowsPerPageOptions={[10, 25, 50, 100]}
          paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
        >
          <Column
            field="scanner_type"
            header="Scanner"
            body={(row: Scan) =>
              tableLoading ? (
                <Skeleton height="16px" />
              ) : row.scanner_type ? (
                row.scanner_type.charAt(0).toUpperCase() + row.scanner_type.slice(1)
              ) : (
                "--"
              )
            }
          />
          <Column
            field="status"
            header="Status"
            body={(row: Scan) =>
              tableLoading ? <Skeleton height="26px" width="100px" /> : statusTemplate(row)
            }
          />
          <Column
            field="vulnerability_count"
            header="Vulnerabilities"
            body={(row: Scan) =>
              tableLoading ? <Skeleton height="16px" /> : vulnCountTemplate(row)
            }
          />
          {showInstructions && (
            <Column
              field="updated_instructions"
              header="Instructions"
              body={(row: Scan) =>
                tableLoading ? <Skeleton height="16px" /> : instructionsTemplate(row)
              }
            />
          )}
          <Column
            field="created_at"
            header="Created At"
            sortable
            body={(row: Scan) => (tableLoading ? <Skeleton height="16px" /> : dateTemplate(row))}
          />
        </DataTable>
      </Card>

      <Dialog
        header="Vulnerabilities"
        visible={!!selectedVulns}
        onHide={() => setSelectedVulns(null)}
        style={{ width: "min(90vw, 800px)" }}
        dismissableMask
      >
        {selectedVulns && (
          <DataTable
            value={selectedVulns}
            className="p-datatable-sm"
            tableClassName="dt-responsive-text"
            sortMode="single"
          >
            <Column field="name" header="Name" sortable />
            <Column
              field="cwe"
              header="CWE"
              sortable
              body={(row: Vulnerability) => normalizeCwe(row.cwe)}
            />
            <Column field="description" header="Description" />
            <Column
              field="level"
              header="Severity"
              sortable
              body={(row: Vulnerability) => (
                <Tag
                  value={LEVEL_LABEL[row.level ?? ""] ?? row.level}
                  severity={LEVEL_SEVERITY[row.level ?? ""] ?? "secondary"}
                />
              )}
            />
          </DataTable>
        )}
      </Dialog>

      <Dialog
        header="Updates to instruction file"
        visible={!!selectedInstructions}
        onHide={() => setSelectedInstructions(null)}
        style={{ width: "95vw", maxWidth: "1400px" }}
        draggable={false}
        dismissableMask
      >
        {selectedInstructions && (
          <>
            <div className="flex gap-6 mb-4 body-sm text-secondary">
              <span>
                <strong>Scanner:</strong>{" "}
                {selectedInstructions.scanner_type
                  ? selectedInstructions.scanner_type.charAt(0).toUpperCase() +
                    selectedInstructions.scanner_type.slice(1)
                  : "--"}
              </span>
              <span>
                <strong>Date:</strong> {humanDate(selectedInstructions.created_at ?? "")}
              </span>
            </div>
            <ReactDiffViewer
              oldValue={selectedInstructions.original_instructions ?? ""}
              newValue={selectedInstructions.updated_instructions ?? ""}
              splitView={true}
            />
          </>
        )}
      </Dialog>
    </>
  );
};

export default AspenGuardianScans;
